import React, { useState, useEffect } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import {
  getAllAdherentPrets,
  deleteAdherentPret,
} from "../../../services/adherentpretServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import AddAdherentPretModal from "./AddAdherentPretModal";
import EditAdherentPretModal from "./EditAdherentPretModal";

const AdherentPretPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [prets, setPrets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPret, setCurrentPret] = useState(null); // ✅ manquant précédemment

  useEffect(() => {
    fetchPrets();
  }, []);

  const fetchPrets = async () => {
    try {
      const data = await getAllAdherentPrets();
      setPrets(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des prêts :", error);
      toast.error("Erreur lors de la récupération des prêts");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdherentPret(id);
      setPrets((prev) => prev.filter((pret) => pret.id !== id));
      toast.success("Prêt supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error("Erreur lors de la suppression du prêt");
    }
  };

  const filteredPrets = prets.filter((pret) => {
    const fullName = `${pret.adherent?.prenom || ""} ${
      pret.adherent?.nom || ""
    } ${pret.article?.nom || ""}`;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        ADMINISTRATEUR
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-4 font-montserrat uppercase">
        Prêt de Matériel
      </h2>
      <DashboardNavigation role={userRole} />

      <div className="text-center p-4 bg-blue-100 rounded-lg shadow max-w-[800px] mx-auto mb-4 dark:text-gray-800 font-montserrat">
        <p className="text-lg font-semibold mb-2">Instructions</p>
        <p className="text-sm text-left">
          Vous pouvez gérer sur cette page les prêts de matériel aux adhérents.
          Le matériel doit être créé dans Articles Admin (article non actif,
          avec la catégorie `Prêt`)
        </p>
      </div>

      <div className="flex flex-col items-center w-full px-4 mt-4">
        <input
          type="text"
          placeholder="Rechercher par nom ou matériel"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-4 p-2 border border-gray-300 rounded w-full sm:w-1/2 md:w-1/3 dark:bg-gray-800 dark:text-white"
        />

        <Button
          text="Ajouter un prêt"
          onClick={() => setShowAddModal(true)}
          className="mb-4"
        />

        <PretTable
          prets={filteredPrets}
          onDelete={handleDelete}
          setCurrentPret={setCurrentPret}
          setShowEditModal={setShowEditModal}
        />
      </div>

      {showAddModal && (
        <AddAdherentPretModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          refreshList={fetchPrets}
        />
      )}

      {showEditModal && currentPret && (
        <EditAdherentPretModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          pret={currentPret}
          refreshList={fetchPrets}
        />
      )}

      <ToastContainer />
    </div>
  );
};

const PretTable = ({ prets, onDelete, setCurrentPret, setShowEditModal }) => {
  return (
    <div className="bg-gray-100 dark:bg-gray-700 shadow-lg w-full max-w-6xl font-montserrat">
      <div className="overflow-x-auto">
        <table className="table-auto w-full bg-white dark:bg-slate-300 border-collapse">
          <thead className="bg-gray-800 text-white text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-white">Adhérent</th>
              <th className="px-4 py-2 text-white">Matériel</th>
              <th className="px-4 py-2 text-white">Date Prêt</th>
              <th className="px-4 py-2 text-white">Date Remise</th>
              <th className="px-4 py-2 text-white">Note</th>
              <th className="px-4 py-2 text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {prets.length > 0 ? (
              prets.map((pret) => (
                <tr
                  key={pret.id}
                  className="text-center text-sm dark:text-black"
                >
                  <td className="px-4 py-2">
                    {pret.adherent?.prenom} {pret.adherent?.nom}
                  </td>
                  <td className="px-4 py-2">{pret.article?.nom}</td>
                  <td className="px-4 py-2">
                    {moment(pret.date_pret).isValid()
                      ? moment(pret.date_pret).format("DD/MM/YY")
                      : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {moment(pret.date_remise).isValid() ? (
                      moment(pret.date_remise).format("DD/MM/YY")
                    ) : (
                      <span className="text-orange-600 font-semibold">
                        En cours
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {pret.note_pret ? pret.note_pret : "Aucune note"}
                  </td>
                  <td className="px-4 py-2 flex justify-center items-center space-x-2">
                    <ButtonEdit
                      onClick={() => {
                        setCurrentPret(pret);
                        setShowEditModal(true);
                      }}
                    />
                    <ButtonDelete onClick={() => onDelete(pret.id)} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-4 text-gray-700">
                  Aucun prêt enregistré.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdherentPretPage;
